import React, { useState, useMemo } from 'react';
import { formatRupiah, formatRupiahFull } from '../data/financialData';
import type { Area } from '../data/financialData';
import { Doughnut, Line } from 'react-chartjs-2';
import { useTheme } from '../ThemeContext';
import { useData } from '../DataContext';
import { parseGLRows, GLRow } from '../utils/budgetMatch';
import { useSmartInsights } from '../SmartInsightsContext';
import SmartTooltip from './SmartTooltip';
import { analyzeKPI, analyzeChartTrend } from '../utils/smartInsightsEngine';
import {
  Chart as ChartJS, ArcElement, CategoryScale, LinearScale, PointElement, LineElement,
  Title, Tooltip, Legend, Filler,
} from 'chart.js';

ChartJS.register(ArcElement, CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend, Filler);

interface ProfitLossPageProps {
  area: Area;
  branch: string;
  startIdx: number;
  endIdx: number;
  activeEntity?: string;
}

export const ProfitLossPage: React.FC<ProfitLossPageProps> = ({
  area,
  branch,
  startIdx,
  endIdx,
  activeEntity
}) => {
  const [mode, setMode] = useState<'consolidated' | 'branch'>('consolidated');
  const [selectedBranch, setSelectedBranch] = useState<string>(branch !== 'ALL' ? branch : '');
  const [monthIdx, setMonthIdx] = useState(endIdx);
  const { chart: chartTheme } = useTheme();
  const { MONTHS, branches, profitLoss, profitLossByBranch, getKPIs } = useData();
  const { isEnabled: insightsEnabled } = useSmartInsights();

  // Active Entity Color Palette
  const accentColor = activeEntity === 'JUARA_GADAI' ? '#F68B1F' : '#10b981';
  const accentLight = activeEntity === 'JUARA_GADAI' ? '#FBA94D' : '#34d399';
  const accentGlow = activeEntity === 'JUARA_GADAI' ? 'rgba(246, 139, 31, 0.15)' : 'rgba(16, 185, 129, 0.15)';

  // Drilldown states
  const [glData, setGlData] = useState<{ columns: string[]; data: any[][] } | null>(null);
  const [breakdownData, setBreakdownData] = useState<Record<string, GLRow[]>>({});
  const [glLoading, setGlLoading] = useState(false);
  const [drilldownTitle, setDrilldownTitle] = useState<string>('');
  const [drilldownRows, setDrilldownRows] = useState<GLRow[]>([]);
  const [showModal, setShowModal] = useState(false);

  // Expandable sub-accounts states
  const [expandedSubAccounts, setExpandedSubAccounts] = useState<Record<string, boolean>>({});
  const [loadingBreakdown, setLoadingBreakdown] = useState<Record<string, boolean>>({});
  const [copied, setCopied] = useState(false);

  // Consolidated view data
  const consolidatedPL = useMemo(() => {
    const areaKey = area as string;
    const pl = (profitLoss as any)[areaKey] ?? (profitLoss as any)['ALL'];
    if (!pl) return null;
    const idx = monthIdx;
    return {
      revenue: {
        interestIncome: pl.revenue.interestIncome[idx] || 0,
        adminFee: pl.revenue.adminFee[idx] || 0,
        gainLelang: pl.revenue.gainLelang[idx] || 0,
        lateFee: pl.revenue.lateFee[idx] || 0,
        otherInterest: pl.revenue.otherInterest[idx] || 0,
        accruedRevenue: pl.revenue.accruedRevenue?.[idx] || 0,
        total: pl.revenue.total[idx] || 0,
      },
      cogs: {
        repairLelang: pl.cogs.repairLelang?.[idx] || 0,
        freightIn: pl.cogs.freightIn?.[idx] || 0,
        interestExpense: pl.cogs.interestExpense?.[idx] || pl.cogs.bunga?.[idx] || 0,
        total: pl.cogs.total[idx] || 0,
      },
      grossProfit: pl.grossProfit[idx] || 0,
      opex: {
        personnel: pl.opex.personnel?.total?.[idx] || 0,
        officeOps: pl.opex.officeOps?.total?.[idx] || 0,
        depreciation: pl.opex.depreciation?.total?.[idx] || 0,
        travel: pl.opex.travel?.total?.[idx] || 0,
        otherOpex: pl.opex.otherOpex?.total?.[idx] || 0,
        total: pl.opex.total[idx] || 0,
      },
      operatingProfit: pl.operatingProfit[idx] || 0,
      otherIncomeExpense: pl.otherIncomeExpense[idx] || 0,
      netProfit: pl.netProfit[idx] || 0,
    };
  }, [area, monthIdx, profitLoss]);

  // Branch view data
  const branchPL = useMemo(() => {
    if (!selectedBranch) return null;
    const pl = (profitLossByBranch as any)[selectedBranch];
    if (!pl) return null;
    const idx = monthIdx;
    return {
      revenue: {
        interestIncome: pl.revenue.interestIncome[idx] || 0,
        adminFee: pl.revenue.adminFee[idx] || 0,
        gainLelang: pl.revenue.gainLelang[idx] || 0,
        lateFee: pl.revenue.lateFee[idx] || 0,
        otherInterest: pl.revenue.otherInterest[idx] || 0,
        accruedRevenue: pl.revenue.accruedRevenue?.[idx] || 0,
        total: pl.revenue.total[idx] || 0,
      },
      cogs: {
        repairLelang: pl.cogs.repairLelang?.[idx] || 0,
        freightIn: pl.cogs.freightIn?.[idx] || 0,
        interestExpense: pl.cogs.interestExpense?.[idx] || pl.cogs.bunga?.[idx] || 0,
        total: pl.cogs.total[idx] || 0,
      },
      grossProfit: pl.grossProfit[idx] || 0,
      opex: {
        personnel: pl.opex.personnel?.total?.[idx] || 0,
        officeOps: pl.opex.officeOps?.total?.[idx] || 0,
        depreciation: pl.opex.depreciation?.total?.[idx] || 0,
        travel: pl.opex.travel?.total?.[idx] || 0,
        otherOpex: pl.opex.otherOpex?.total?.[idx] || 0,
        total: pl.opex.total[idx] || 0,
      },
      operatingProfit: pl.operatingProfit[idx] || 0,
      otherIncomeExpense: pl.otherIncomeExpense[idx] || 0,
      netProfit: pl.netProfit[idx] || 0,
    };
  }, [selectedBranch, monthIdx, profitLossByBranch]);

  const activePL = mode === 'consolidated' ? consolidatedPL : branchPL;

  // Margin ratios
  const margins = useMemo(() => {
    if (!activePL) return null;
    const rev = activePL.revenue.total;
    const gp = activePL.grossProfit;
    const np = activePL.netProfit;
    const gpMargin = rev > 0 ? gp / rev : 0;
    const npMargin = rev > 0 ? np / rev : 0;
    const opexRatio = rev > 0 ? activePL.opex.total / rev : 0;

    return { gpMargin, npMargin, opexRatio };
  }, [activePL]);

  // Donut: Revenue Composition
  const revDonut = useMemo(() => {
    if (!activePL) return null;
    return {
      labels: ['Interest Income', 'Admin Fee', 'Gain Lelang', 'Late Fee', 'Other Interest', 'Accrued Revenue'],
      datasets: [{
        data: [
          Math.abs(activePL.revenue.interestIncome),
          Math.abs(activePL.revenue.adminFee),
          Math.abs(activePL.revenue.gainLelang),
          Math.abs(activePL.revenue.lateFee),
          Math.abs(activePL.revenue.otherInterest),
          Math.abs(activePL.revenue.accruedRevenue),
        ],
        backgroundColor: ['#10b981', '#3b82f6', '#f59e0b', '#ef4444', activeEntity === 'JUARA_GADAI' ? '#303795' : '#8b5cf6', '#06b6d4'],
        borderColor: 'transparent',
        borderWidth: 2,
      }],
    };
  }, [activePL]);

  // Donut: OpEx Composition
  const opexDonut = useMemo(() => {
    if (!activePL) return null;
    return {
      labels: ['Personnel', 'Office & Ops', 'Depreciation', 'Travel', 'Other OpEx'],
      datasets: [{
        data: [
          Math.abs(activePL.opex.personnel),
          Math.abs(activePL.opex.officeOps),
          Math.abs(activePL.opex.depreciation),
          Math.abs(activePL.opex.travel),
          Math.abs(activePL.opex.otherOpex),
        ],
        backgroundColor: ['#3b82f6', '#f59e0b', activeEntity === 'JUARA_GADAI' ? '#303795' : '#8b5cf6', '#06b6d4', '#cbd5e1'],
        borderColor: 'transparent',
        borderWidth: 2,
      }],
    };
  }, [activePL]);

  // Trend line chart data
  const trendData = useMemo(() => {
    const areaKey = area as string;
    const pl = (profitLoss as any)[areaKey] ?? (profitLoss as any)['ALL'];
    if (!pl) return null;

    const labels = MONTHS.slice(startIdx, endIdx + 1).map(m => m.label);
    return {
      labels,
      datasets: [
        {
          label: 'Total Revenue',
          data: pl.revenue.total.slice(startIdx, endIdx + 1),
          borderColor: '#10b981',
          backgroundColor: 'rgba(16,185,129,0.1)',
          fill: true, tension: 0.3, pointRadius: 2,
        },
        {
          label: 'Total OpEx',
          data: pl.opex.total.slice(startIdx, endIdx + 1),
          borderColor: '#ef4444',
          backgroundColor: 'rgba(239,68,68,0.1)',
          fill: true, tension: 0.3, pointRadius: 2,
        },
        {
          label: 'Net Profit',
          data: pl.netProfit.slice(startIdx, endIdx + 1),
          borderColor: activeEntity === 'JUARA_GADAI' ? '#F68B1F' : '#8b5cf6',
          backgroundColor: activeEntity === 'JUARA_GADAI' ? 'rgba(246, 139, 31, 0.1)' : 'rgba(139, 92, 246, 0.1)',
          fill: true, tension: 0.3, pointRadius: 2,
        },
      ],
    };
  }, [area, startIdx, endIdx, MONTHS, profitLoss]);

  const donutOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: { display: true, position: 'bottom' as const, labels: { color: chartTheme.legendColor, font: { size: 10 }, padding: 12 } },
      tooltip: {
        backgroundColor: chartTheme.tooltipBg, titleColor: chartTheme.tooltipTitle,
        bodyColor: chartTheme.tooltipBody, borderColor: chartTheme.tooltipBorder, borderWidth: 1,
        callbacks: { label: (ctx: any) => `${ctx.label}: ${formatRupiah(ctx.raw)}` },
      },
      datalabels: { display: false },
    },
    cutout: '65%',
  };

  const handleDrilldown = async (title: string, codes: string[], matches?: (code: string) => boolean) => {
    setDrilldownTitle(title);
    setDrilldownRows([]);
    setGlLoading(true);
    setShowModal(true);

    try {
      const API_KEY = process.env.REACT_APP_BUDGET_API_KEY ?? '';
      const API_BASE = process.env.REACT_APP_BUDGET_API_BASE ?? '';
      
      const targetMonth = MONTHS[monthIdx].month;
      const qParams = new URLSearchParams({
        entity: activeEntity ?? 'JUARA_GADAI',
        mo: targetMonth,
        acc_code_prefixes: codes.join(',')
      });
      if (mode === 'branch' && selectedBranch) {
        qParams.set('branch', selectedBranch);
      } else if (mode !== 'branch' && area !== 'ALL') {
        qParams.set('area', area);
      }

      const res = await fetch(`${API_BASE}/api/gl-transactions?${qParams.toString()}`, {
        headers: { 'Authorization': `Bearer ${API_KEY}` }
      });
      if (!res.ok) throw new Error('Gagal memuat detail jurnal Buku Besar dari cloud.');
      const loadedData = await res.json();
      const parsedAll = parseGLRows(loadedData.data);

      const filtered = parsedAll.filter((row) => {
        if (matches) {
          return matches(row.accCode);
        }
        return true;
      });

      setDrilldownRows(filtered);
    } catch (err: any) {
      console.error(err);
      alert(err.message || 'Terjadi kesalahan saat memuat drilldown.');
      setShowModal(false);
    } finally {
      setGlLoading(false);
    }
  };

  const toggleExpand = async (key: string, prefixes: string[]) => {
    if (expandedSubAccounts[key]) {
      setExpandedSubAccounts(prev => ({ ...prev, [key]: false }));
      return;
    }

    if (!breakdownData[key]) {
      setLoadingBreakdown(prev => ({ ...prev, [key]: true }));
      try {
        const API_KEY = process.env.REACT_APP_BUDGET_API_KEY ?? '';
        const API_BASE = process.env.REACT_APP_BUDGET_API_BASE ?? '';
        
        const targetMonth = MONTHS[monthIdx].month;
        const qParams = new URLSearchParams({
          entity: activeEntity ?? 'JUARA_GADAI',
          mo: targetMonth,
          acc_code_prefixes: prefixes.join(',')
        });
        if (mode === 'branch' && selectedBranch) {
          qParams.set('branch', selectedBranch);
        } else if (mode !== 'branch' && area !== 'ALL') {
          qParams.set('area', area);
        }

        const res = await fetch(`${API_BASE}/api/gl-transactions?${qParams.toString()}`, {
          headers: { 'Authorization': `Bearer ${API_KEY}` }
        });
        if (!res.ok) throw new Error('Gagal memuat detail sub account dari cloud.');
        const loadedData = await res.json();
        const parsed = parseGLRows(loadedData.data);
        setBreakdownData(prev => ({ ...prev, [key]: parsed }));
      } catch (err: any) {
        console.error(err);
        alert(err.message || 'Terjadi kesalahan saat memuat detail.');
        return;
      } finally {
        setLoadingBreakdown(prev => ({ ...prev, [key]: false }));
      }
    }

    setExpandedSubAccounts(prev => ({ ...prev, [key]: true }));
  };

  const handleCopyTable = async () => {
    const tableEl = document.querySelector('.paper-table');
    if (!tableEl) return;

    try {
      const clonedTable = tableEl.cloneNode(true) as HTMLTableElement;
      clonedTable.querySelectorAll('.expand-btn-toggle').forEach(btn => { btn.remove(); });
      clonedTable.querySelectorAll('td, th').forEach((cell: any) => {
        cell.style.fontFamily = 'Arial, sans-serif';
        if (cell.style.textAlign === 'right') cell.setAttribute('align', 'right');
      });

      const htmlContent = `
        <html>
        <head>
          <style>
            table { border-collapse: collapse; font-family: Arial, sans-serif; font-size: 11pt; width: 100%; }
            th { border-bottom: 2px solid #000; font-weight: bold; padding: 8px; text-align: left; }
            td { border-bottom: 1px solid #ddd; padding: 8px; text-align: left; }
            .paper-header-row td { font-weight: bold; background-color: #f8fafc; border-bottom: 2px solid #000; }
            .paper-total-row td { font-weight: bold; border-top: 1px solid #000; border-bottom: 4px double #000; }
          </style>
        </head>
        <body>
          <div style="text-align: center; margin-bottom: 20px;">
            <h3 style="margin: 0; font-size: 16pt;">LAPORAN LABA RUGI</h3>
            <span style="font-size: 11pt; color: #64748b; font-weight: bold;">
              ${activeEntity === 'JUARA_GADAI' ? 'JUARA GADAI GROUP' : 'SAHABAT GADAI'}
            </span>
            <div style="font-size: 10pt; color: #64748b;">
              Konsolidasi per ${MONTHS[monthIdx]?.label}
            </div>
          </div>
          ${clonedTable.outerHTML}
        </body>
        </html>
      `;

      let plainText = `LAPORAN LABA RUGI - ${activeEntity === 'JUARA_GADAI' ? 'JUARA GADAI GROUP' : 'SAHABAT GADAI'}\n`;
      plainText += `Konsolidasi per ${MONTHS[monthIdx]?.label}\n\n`;

      const rows = tableEl.querySelectorAll('tr');
      rows.forEach(row => {
        const cells = row.querySelectorAll('th, td');
        const rowData: string[] = [];
        cells.forEach((cell: any) => {
          let text = cell.innerText || cell.textContent || '';
          text = text.replace(/[▼▶]/g, '').trim();
          rowData.push(text);
        });
        if (rowData.length > 0) plainText += rowData.join('\t') + '\n';
      });

      const blobHtml = new Blob([htmlContent], { type: 'text/html' });
      const blobText = new Blob([plainText], { type: 'text/plain' });

      await navigator.clipboard.write([
        new ClipboardItem({ 'text/html': blobHtml, 'text/plain': blobText })
      ]);

      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy profit and loss table:', err);
      alert('Gagal menyalin tabel ke clipboard.');
    }
  };

  const renderSubAccountRow = (
    key: string,
    label: string,
    value: number,
    prefixes: string[],
    customMatches?: (code: string) => boolean,
    isNegative = false
  ) => {
    const isExpanded = expandedSubAccounts[key];
    const isLoading = loadingBreakdown[key];

    const getBreakdown = () => {
      const filtered = breakdownData[key] || [];

      const agg: Record<string, { code: string; name: string; balance: number }> = {};
      filtered.forEach(row => {
        if (!agg[row.accCode]) {
          agg[row.accCode] = { code: row.accCode, name: row.accName, balance: 0 };
        }
        // Revenue is Credit - Debit. COGS and OpEx are Debit - Credit.
        let val = 0;
        if (prefixes.some(p => p.startsWith('10'))) {
          val = row.credit - row.debit;
        } else {
          val = row.debit - row.credit;
        }
        agg[row.accCode].balance += val;
      });

      return Object.values(agg)
        .filter(x => Math.abs(x.balance) > 0.01)
        .sort((a, b) => a.code.localeCompare(b.code));
    };

    const breakdownItems = isExpanded ? getBreakdown() : [];

    return (
      <>
        <tr>
          <td style={{ paddingLeft: '40px', color: '#475569', display: 'flex', alignItems: 'center', gap: '6px' }}>
            <button
              onClick={() => toggleExpand(key, prefixes)}
              style={{
                background: 'none', border: 'none', color: accentColor, cursor: 'pointer',
                padding: '0 4px', fontSize: '11px', fontWeight: 'bold', display: 'inline-flex',
                alignItems: 'center', justifyContent: 'center', width: '18px', height: '18px',
                borderRadius: '4px', transition: 'background 0.2s',
              }}
              className="expand-btn-toggle"
            >
              {isLoading ? <span className="mini-spinner" /> : isExpanded ? '▼' : '▶'}
            </button>
            <span style={{ cursor: 'pointer' }} onClick={() => toggleExpand(key, prefixes)}>
              {label}
            </span>
          </td>
          <td style={{ textAlign: 'right', color: isNegative ? '#ef4444' : 'inherit' }}>
            <span className="drillable-value" onClick={() => handleDrilldown(label, prefixes, customMatches)}>
              {formatRupiah(value)}
            </span>
          </td>
        </tr>

        {isExpanded && breakdownItems.length === 0 && (
          <tr style={{ backgroundColor: 'rgba(0,0,0,0.005)' }}>
            <td colSpan={2} style={{ paddingLeft: '72px', color: 'var(--text-muted)', fontSize: '11px', fontStyle: 'italic' }}>
              Tidak ada data transaksi Buku Besar untuk sub-akun ini.
            </td>
          </tr>
        )}

        {isExpanded && breakdownItems.map(item => (
          <tr key={item.code} style={{ backgroundColor: 'rgba(0,0,0,0.005)', fontSize: '11.5px' }}>
            <td style={{ paddingLeft: '72px', color: 'var(--text-muted)' }}>
              {item.code} — {item.name}
            </td>
            <td style={{ textAlign: 'right', paddingRight: '24px', fontFamily: 'monospace' }}>
              <span className="drillable-value" onClick={() => handleDrilldown(`${item.code} — ${item.name}`, [item.code])}>
                {formatRupiah(item.balance)}
              </span>
            </td>
          </tr>
        ))}
      </>
    );
  };

  return (
    <div className="data-table-card animate-in">
      {/* Header Panel */}
      <div className="data-table-header">
        <div>
          <div className="data-table-title" style={{ fontSize: '20px' }}>📈 Profit and Loss (Laba Rugi)</div>
          <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>
            Laporan Kinerja Laba Rugi — s/d {MONTHS[monthIdx]?.label}
          </div>
        </div>

        {/* Mode Switcher */}
        <div style={{ display: 'flex', gap: '8px' }}>
          {(['consolidated', 'branch'] as const).map(v => (
            <button
              key={v}
              onClick={() => setMode(v)}
              style={{
                padding: '6px 14px',
                fontSize: '12px',
                fontWeight: '600',
                borderRadius: '6px',
                border: '1px solid var(--border-light)',
                cursor: 'pointer',
                backgroundColor: mode === v ? accentColor : 'var(--bg-secondary)',
                color: mode === v ? '#fff' : 'var(--text-muted)',
                transition: 'all 0.15s ease', fontFamily: 'Inter, sans-serif',
              }}
            >
              {v === 'consolidated' ? '🏢 Konsolidasi' : 'Per Cabang'}
            </button>
          ))}
        </div>
      </div>

      {/* Filters Panel */}
      <div style={{ padding: '16px 24px', display: 'flex', gap: '16px', borderBottom: '1px solid var(--border-color)', background: 'rgba(255,255,255,0.01)' }}>
        <div style={{ flex: '0 0 auto' }}>
          <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>Bulan Laporan</label>
          <select
            value={monthIdx}
            onChange={e => setMonthIdx(parseInt(e.target.value))}
            style={{
              padding: '8px 12px', fontSize: '13px', border: '1px solid var(--border-color)',
              borderRadius: '6px', backgroundColor: 'var(--bg-input)', color: 'var(--text-primary)',
              cursor: 'pointer', outline: 'none'
            }}
          >
            {MONTHS.slice(startIdx, endIdx + 1).map((m, i) => (
              <option key={startIdx + i} value={startIdx + i}>{m.label}</option>
            ))}
          </select>
        </div>

        {mode === 'branch' && (
          <div style={{ flex: '0 0 auto' }}>
            <label style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-muted)', display: 'block', marginBottom: '4px', textTransform: 'uppercase' }}>Pilih Cabang</label>
            <select
              value={selectedBranch}
              onChange={e => setSelectedBranch(e.target.value)}
              style={{
                padding: '8px 12px', fontSize: '13px', border: '1px solid var(--border-color)',
                borderRadius: '6px', backgroundColor: 'var(--bg-input)', color: 'var(--text-primary)',
                cursor: 'pointer', outline: 'none'
              }}
            >
              <option value="">Pilih Cabang</option>
              {branches.map(b => (
                <option key={b.code} value={b.code}>{b.name} ({b.code})</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {!activePL ? (
        <div style={{ padding: '40px', color: 'var(--text-muted)', textAlign: 'center' }}>
          {mode === 'branch' ? '⚠️ Pilih cabang terlebih dahulu untuk memuat Laporan Laba Rugi.' : 'Data tidak tersedia'}
        </div>
      ) : (
        <>
          {/* KPI Summary Cards */}
          <div style={{ padding: '20px 24px', display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '14px' }}>
            {[
              { label: 'Total Revenue', value: activePL.revenue.total, color: '#10b981' },
              { label: 'Gross Profit', value: activePL.grossProfit, color: '#3b82f6' },
              { label: 'Total OpEx', value: activePL.opex.total, color: '#ef4444' },
              { label: 'Net Profit', value: activePL.netProfit, color: activePL.netProfit >= 0 ? '#10b981' : '#ef4444' },
              { label: 'Gross Profit Margin', value: (margins?.gpMargin || 0) * 100, isPercent: true, color: '#3b82f6' },
              { label: 'Net Profit Margin', value: (margins?.npMargin || 0) * 100, isPercent: true, color: activeEntity === 'JUARA_GADAI' ? '#F68B1F' : '#8b5cf6' },
            ].map((item, i) => {
              const itemInsights = insightsEnabled ? analyzeKPI({
                title: item.label,
                value: item.value,
                prevValue: undefined,
                kpis: getKPIs('ALL', 0, monthIdx),
                prevKpis: null
              }) : [];

              return (
                <SmartTooltip key={i} insights={itemInsights} title={item.label}>
                  <div style={{ backgroundColor: 'var(--bg-secondary)', padding: '14px', borderRadius: '8px', border: '1px solid var(--border-light)' }}>
                    <div style={{ fontSize: '11px', color: 'var(--text-muted)', marginBottom: '4px', fontWeight: '600' }}>{item.label}</div>
                    <div style={{ fontSize: '16px', fontWeight: 'bold', color: item.color }}>
                      {(item as any).isPercent ? `${item.value.toFixed(1)}%` : formatRupiah(item.value as number)}
                    </div>
                  </div>
                </SmartTooltip>
              );
            })}
          </div>

          {/* Donut Charts & Trend */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(360px, 1fr))', gap: '20px', padding: '0 24px 20px' }}>
            <div style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '12px', padding: '16px', border: '1px solid var(--border-light)' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>📊 Komposisi Revenue</div>
              <div style={{ height: '200px' }}>
                {revDonut && <Doughnut data={revDonut} options={donutOptions} />}
              </div>
            </div>
            <div style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '12px', padding: '16px', border: '1px solid var(--border-light)' }}>
              <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>📊 Distribusi OpEx</div>
              <div style={{ height: '200px' }}>
                {opexDonut && <Doughnut data={opexDonut} options={donutOptions} />}
              </div>
            </div>
          </div>

          {/* Trend Chart (Consolidated only) */}
          {mode === 'consolidated' && trendData && (
            <div style={{ padding: '0 24px 24px' }}>
              <div style={{ backgroundColor: 'var(--bg-secondary)', borderRadius: '12px', padding: '16px', border: '1px solid var(--border-light)' }}>
                <div style={{ fontSize: '13px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '12px' }}>📈 Trend Kinerja Keuangan Bulanan</div>
                <SmartTooltip 
                  insights={insightsEnabled ? analyzeChartTrend(trendData.datasets[0].data, trendData.labels, 'Total Revenue') : []}
                  title="Trend Revenue Bulanan"
                >
                  <div style={{ height: '220px' }}>
                    <Line
                      data={trendData}
                      options={{
                        responsive: true, maintainAspectRatio: false,
                        plugins: {
                          legend: { display: true, labels: { color: chartTheme.legendColor, font: { size: 10 } } },
                          tooltip: {
                            backgroundColor: chartTheme.tooltipBg, titleColor: chartTheme.tooltipTitle,
                            bodyColor: chartTheme.tooltipBody, borderColor: chartTheme.tooltipBorder, borderWidth: 1,
                            callbacks: { label: (ctx: any) => `${ctx.dataset.label}: ${formatRupiah(ctx.raw)}` },
                          },
                          datalabels: { display: false },
                        },
                        scales: {
                          x: { ticks: { color: chartTheme.tickColor, font: { size: 9 } }, grid: { color: chartTheme.gridColorX } },
                          y: {
                            ticks: { color: chartTheme.tickColor, font: { size: 9 }, callback: (v: any) => `${(v / 1e6).toFixed(0)} Jt` },
                            grid: { color: chartTheme.gridColorY },
                          },
                        },
                      }}
                    />
                  </div>
                </SmartTooltip>
              </div>
            </div>
          )}

          {/* TABLE - PAPER STYLE */}
          <div style={{ padding: '0 24px 32px' }}>
            <div className="paper-report-sheet" style={{ position: 'relative' }}>
              <button 
                className={`paper-copy-btn ${copied ? 'copied' : ''}`}
                onClick={handleCopyTable}
                title="Salin laporan laba rugi"
              >
                {copied ? (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ transform: 'scale(1.1)' }}>
                      <polyline points="20 6 9 17 4 12"></polyline>
                    </svg>
                    <span>Tersalin!</span>
                  </>
                ) : (
                  <>
                    <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="9" y="9" width="13" height="13" rx="2" ry="2"></rect>
                      <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"></path>
                    </svg>
                    <span>Salin Tabel</span>
                  </>
                )}
              </button>

              <div style={{ textAlign: 'center', marginBottom: '24px', borderBottom: '1px solid #e2e8f0', paddingBottom: '16px' }}>
                <h3 style={{ margin: '0', fontSize: '18px', fontWeight: '800', letterSpacing: '0.5px' }}>LAPORAN LABA RUGI</h3>
                <span style={{ fontSize: '12px', color: '#64748b', fontWeight: '500', textTransform: 'uppercase' }}>
                  {activeEntity === 'JUARA_GADAI' ? 'JUARA GADAI GROUP' : 'SAHABAT GADAI'}
                </span>
                <div style={{ fontSize: '11px', color: '#64748b', marginTop: '4px' }}>
                  Konsolidasi per {MONTHS[monthIdx]?.label} (Mata Uang: IDR)
                </div>
              </div>

              <table className="paper-table">
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left' }}>DESKRIPSI POS LABA RUGI</th>
                    <th style={{ textAlign: 'right', width: '220px' }}>NOMINAL RUPIAH</th>
                  </tr>
                </thead>
                <tbody>
                  {/* REVENUE */}
                  <tr className="paper-header-row">
                    <td>PENDAPATAN OPERASIONAL (OPERATING REVENUE)</td>
                    <td />
                  </tr>
                  <tr style={{ fontWeight: '700', backgroundColor: 'rgba(0,0,0,0.01)' }}>
                    <td style={{ paddingLeft: '24px' }}>Total Pendapatan (Total Revenue)</td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="drillable-value" onClick={() => handleDrilldown('Total Pendapatan Operasional', ['10'])}>
                        {formatRupiah(activePL.revenue.total)}
                      </span>
                    </td>
                  </tr>
                  {renderSubAccountRow('revInterest', 'Interest Income', activePL.revenue.interestIncome, ['101'])}
                  {renderSubAccountRow('revAdmin', 'Admin Fee', activePL.revenue.adminFee, ['102'])}
                  {renderSubAccountRow('revLelang', 'Gain Lelang', activePL.revenue.gainLelang, ['103'])}
                  {renderSubAccountRow('revLate', 'Late Fee', activePL.revenue.lateFee, ['104'])}
                  {renderSubAccountRow('revOther', 'Other Interest', activePL.revenue.otherInterest, ['105'])}
                  {renderSubAccountRow('revAccrued', 'Accrued Revenue', activePL.revenue.accruedRevenue, ['106'])}

                  {/* Spacer */}
                  <tr style={{ height: '12px', border: 'none' }}><td colSpan={2} style={{ border: 'none' }} /></tr>

                  {/* COGS */}
                  <tr className="paper-header-row">
                    <td>HARGA POKOK PENJUALAN / BIAYA LANGSUNG (COGS)</td>
                    <td />
                  </tr>
                  <tr style={{ fontWeight: '700', backgroundColor: 'rgba(0,0,0,0.01)' }}>
                    <td style={{ paddingLeft: '24px' }}>Total Biaya Langsung (Total COGS)</td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="drillable-value" onClick={() => handleDrilldown('Total Harga Pokok Penjualan (COGS)', ['11'])}>
                        {formatRupiah(activePL.cogs.total)}
                      </span>
                    </td>
                  </tr>
                  {renderSubAccountRow('cogsRepair', 'Repair Lelang', activePL.cogs.repairLelang, ['11101'])}
                  {renderSubAccountRow('cogsFreight', 'Freight In', activePL.cogs.freightIn, ['11102'])}
                  {renderSubAccountRow('cogsBunga', 'Bunga / Interest Expense', activePL.cogs.interestExpense, ['11103'])}

                  {/* GROSS PROFIT ROW */}
                  <tr style={{ fontWeight: '800', backgroundColor: 'rgba(0,0,0,0.02)', borderTop: '2px solid #000', borderBottom: '2px solid #000' }}>
                    <td style={{ paddingLeft: '24px', color: accentColor }}>LABA KOTOR (GROSS PROFIT)</td>
                    <td style={{ textAlign: 'right', fontWeight: '800', color: activePL.grossProfit >= 0 ? 'inherit' : '#ef4444' }}>
                      {formatRupiah(activePL.grossProfit)}
                    </td>
                  </tr>

                  {/* Spacer */}
                  <tr style={{ height: '12px', border: 'none' }}><td colSpan={2} style={{ border: 'none' }} /></tr>

                  {/* OPEX */}
                  <tr className="paper-header-row">
                    <td>BIAYA OPERASIONAL (OPERATING EXPENSES)</td>
                    <td />
                  </tr>
                  <tr style={{ fontWeight: '700', backgroundColor: 'rgba(0,0,0,0.01)' }}>
                    <td style={{ paddingLeft: '24px' }}>Total Biaya Operasional (Total OpEx)</td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="drillable-value" onClick={() => handleDrilldown('Total Pengeluaran Operasional', ['12'])}>
                        {formatRupiah(activePL.opex.total)}
                      </span>
                    </td>
                  </tr>
                  {renderSubAccountRow('opexPersonnel', 'Personnel Expense (Gaji & BPJS)', activePL.opex.personnel, ['121', '122', '123', '124', '125'])}
                  {renderSubAccountRow('opexOffice', 'Office & Operations (Ruko & Ops)', activePL.opex.officeOps, ['126'])}
                  {renderSubAccountRow('opexDepre', 'Depreciation Expense', activePL.opex.depreciation, ['127'])}
                  {renderSubAccountRow('opexTravel', 'Travel & Transport', activePL.opex.travel, ['128'])}
                  {renderSubAccountRow('opexOther', 'Other General Expenses', activePL.opex.otherOpex, ['12'])}

                  {/* OPERATING PROFIT ROW */}
                  <tr style={{ fontWeight: '800', backgroundColor: 'rgba(0,0,0,0.02)', borderTop: '2px solid #000', borderBottom: '2px solid #000' }}>
                    <td style={{ paddingLeft: '24px', color: accentColor }}>LABA OPERASIONAL (OPERATING PROFIT)</td>
                    <td style={{ textAlign: 'right', fontWeight: '800', color: activePL.operatingProfit >= 0 ? 'inherit' : '#ef4444' }}>
                      {formatRupiah(activePL.operatingProfit)}
                    </td>
                  </tr>

                  {/* Spacer */}
                  <tr style={{ height: '12px', border: 'none' }}><td colSpan={2} style={{ border: 'none' }} /></tr>

                  {/* OTHER INCOME & EXPENSE */}
                  <tr className="paper-header-row">
                    <td>PENDAPATAN/BIAYA NON-OPERASIONAL (OTHER)</td>
                    <td />
                  </tr>
                  <tr style={{ fontWeight: '700', backgroundColor: 'rgba(0,0,0,0.01)' }}>
                    <td style={{ paddingLeft: '24px' }}>Lain-Lain Net (Other Income/Expense Net)</td>
                    <td style={{ textAlign: 'right' }}>
                      <span className="drillable-value" onClick={() => handleDrilldown('Total Pendapatan/Biaya Lain-lain', ['15', '13', '14'])}>
                        {formatRupiah(activePL.otherIncomeExpense)}
                      </span>
                    </td>
                  </tr>
                  {renderSubAccountRow('otherIncExp', 'Other Income/Expense General', activePL.otherIncomeExpense, ['15', '13', '14'])}

                  {/* NET PROFIT ROW (Accounting style double underline) */}
                  <tr className="paper-total-row">
                    <td style={{ paddingLeft: '24px', color: accentLight }}>LABA BERSIH PERIODE BERJALAN (NET PROFIT)</td>
                    <td style={{ textAlign: 'right', fontWeight: '800', color: activePL.netProfit >= 0 ? '#10b981' : '#ef4444' }}>
                      <span className="drillable-value" onClick={() => handleDrilldown('Laba Bersih Seluruh Pos', ['1'])}>
                        {formatRupiahFull(activePL.netProfit)}
                      </span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}

      {/* DRILLDOWN JURNAL MODAL POPUP */}
      {showModal && (
        <div className="drill-modal-overlay" onClick={() => setShowModal(false)}>
          <div className="drill-modal" onClick={(e) => e.stopPropagation()}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', borderBottom: '1px solid var(--border-color)', paddingBottom: '12px' }}>
              <div>
                <h3 style={{ margin: 0, fontSize: '16px', fontWeight: '800', display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <span>📖</span> Drilldown Jurnal Keuangan PNL
                </h3>
                <p style={{ fontSize: '11px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                  Detail baris transaksi untuk: <strong>{drilldownTitle}</strong> ({MONTHS[monthIdx]?.label})
                </p>
              </div>
              <button 
                onClick={() => setShowModal(false)}
                style={{ background: 'none', border: 'none', color: 'var(--text-muted)', fontSize: '18px', cursor: 'pointer' }}
              >
                ✕
              </button>
            </div>

            {glLoading ? (
              <div style={{ padding: '60px', textAlign: 'center' }}>
                <div className="spinner" style={{ margin: '0 auto 16px', width: '36px', height: '36px', borderRadius: '50%', border: '3px solid var(--border-light)', borderTopColor: accentColor, animation: 'spin 1s linear infinite' }} />
                <div style={{ fontSize: '13px', color: 'var(--text-secondary)' }}>Mengekstrak baris jurnal transaksi Buku Besar...</div>
              </div>
            ) : drilldownRows.length === 0 ? (
              <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-secondary)' }}>
                Tidak ditemukan baris transaksi Buku Besar yang cocok pada filter periode/cabang ini.
              </div>
            ) : (
              <div style={{ overflowY: 'auto', flex: 1, maxHeight: '50vh' }}>
                <table className="data-table" style={{ width: '100%', fontSize: '12px' }}>
                  <thead style={{ position: 'sticky', top: 0, background: 'var(--bg-card)', zIndex: 1 }}>
                    <tr>
                      <th style={{ textAlign: 'left' }}>Bulan</th>
                      <th style={{ textAlign: 'left' }}>Nama Akun (COA)</th>
                      <th style={{ textAlign: 'left' }}>Keterangan / Deskripsi</th>
                      <th style={{ textAlign: 'left' }}>Cabang</th>
                      <th style={{ textAlign: 'right' }}>Debit</th>
                      <th style={{ textAlign: 'right' }}>Kredit</th>
                      <th style={{ textAlign: 'right' }}>Net Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {drilldownRows.map((row, idx) => (
                      <tr key={idx}>
                        <td style={{ whiteSpace: 'nowrap' }}>{row.month}</td>
                        <td style={{ fontWeight: '600' }}>{row.accName}</td>
                        <td style={{ color: 'var(--text-accent)', maxWidth: '280px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={row.desc}>
                          {row.desc}
                        </td>
                        <td style={{ fontFamily: 'monospace', fontWeight: '700' }}>{row.branch}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{row.debit > 0 ? formatRupiah(row.debit) : '—'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace' }}>{row.credit > 0 ? formatRupiah(row.credit) : '—'}</td>
                        <td style={{ textAlign: 'right', fontFamily: 'monospace', fontWeight: '700', color: row.amount >= 0 ? 'var(--emerald-light)' : 'var(--red-light)' }}>
                          {formatRupiah(row.amount)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ borderTop: '1px solid var(--border-color)', paddingTop: '12px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', fontSize: '12px' }}>
              <span style={{ color: 'var(--text-secondary)' }}>
                Ditemukan <strong>{drilldownRows.length}</strong> baris transaksi jurnal.
              </span>
              <button className="btn btn-secondary" onClick={() => setShowModal(false)} style={{ padding: '6px 14px', fontSize: '12px' }}>
                Tutup Rincian
              </button>
            </div>
          </div>
        </div>
      )}
      <style dangerouslySetInnerHTML={{__html: `
        .paper-report-sheet {
          background: #ffffff !important;
          color: #1e293b !important;
          border-radius: 16px;
          border: 1px solid #e2e8f0;
          box-shadow: 0 10px 30px rgba(0,0,0,0.02);
          padding: 32px;
          font-family: 'Inter', system-ui, -apple-system, sans-serif;
        }
        .dark .paper-report-sheet {
          background: #0f172a !important;
          color: #f8fafc !important;
          border-color: #334155;
        }
        .paper-table {
          width: 100%;
          border-collapse: collapse;
          font-size: 13.5px;
        }
        .paper-table th {
          border-bottom: 2px solid #1e293b;
          color: #475569;
          font-weight: 700;
          padding: 10px 14px;
        }
        .dark .paper-table th {
          border-color: #f8fafc;
          color: #94a3b8;
        }
        .paper-table td {
          padding: 10px 14px;
          border-bottom: 1px solid #f1f5f9;
        }
        .dark .paper-table td {
          border-bottom-color: #1e293b;
        }
        .paper-header-row {
          font-weight: 700;
          background: rgba(0,0,0,0.01) !important;
          border-bottom: 1px solid #1e293b !important;
          font-size: 14.5px;
        }
        .dark .paper-header-row {
          border-bottom-color: #f8fafc !important;
        }
        .paper-total-row {
          font-weight: 800;
          font-size: 14.5px;
          background: transparent !important;
        }
        .paper-total-row td {
          border-top: 1px solid #1e293b;
          border-bottom: 4px double #1e293b !important;
          padding: 14px;
        }
        .dark .paper-total-row td {
          border-top-color: #f8fafc;
          border-bottom-color: #f8fafc !important;
        }
        .drillable-value {
          font-family: 'monospace';
          cursor: pointer;
          position: relative;
          transition: all 0.2s;
          padding: 2px 6px;
          border-radius: 4px;
          white-space: nowrap;
        }
        .drillable-value:hover {
          color: ${accentColor} !important;
          background: ${accentGlow};
          text-decoration: underline;
        }
        .drill-modal-overlay {
          position: fixed;
          inset: 0;
          left: 260px;
          background: rgba(15, 23, 42, 0.7);
          backdrop-filter: blur(8px);
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 1000;
          animation: fadeIn 0.25s ease;
          transition: left var(--transition-normal);
        }
        .main-content.sidebar-collapsed .drill-modal-overlay {
          left: 80px;
        }
        @media (max-width: 768px) {
          .drill-modal-overlay {
            left: 0 !important;
          }
        }
        .drill-modal {
          background: var(--bg-card);
          border: 1px solid var(--border-color);
          border-radius: 16px;
          padding: 28px;
          width: 900px;
          max-width: 92vw;
          max-height: 80vh;
          box-shadow: var(--shadow-2xl);
          display: flex;
          flex-direction: column;
          gap: 16px;
          animation: slideUp 0.25s cubic-bezier(0.16, 1, 0.3, 1);
        }
        @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
        @keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
        @keyframes spin { to { transform: rotate(360deg); } }
        .mini-spinner {
          width: 10px;
          height: 10px;
          border: 2px solid var(--border-light);
          border-top-color: ${accentColor};
          border-radius: 50%;
          display: inline-block;
          animation: spin 1s linear infinite;
        }
        .expand-btn-toggle:hover {
          background: ${accentGlow};
        }
        .paper-copy-btn {
          position: absolute;
          top: 24px;
          right: 24px;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 6px;
          padding: 8px 14px;
          font-size: 12.5px;
          font-weight: 600;
          border-radius: 8px;
          border: 1px solid #e2e8f0;
          background: #ffffff;
          color: #475569;
          cursor: pointer;
          transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1);
          box-shadow: 0 1px 3px rgba(0,0,0,0.05);
          z-index: 10;
        }
        .dark .paper-copy-btn {
          background: #1e293b;
          border-color: #334155;
          color: #94a3b8;
          box-shadow: 0 1px 3px rgba(0,0,0,0.2);
        }
        .paper-copy-btn:hover {
          background: ${accentGlow};
          color: ${accentColor};
          border-color: ${accentColor};
          transform: translateY(-1px);
          box-shadow: 0 4px 12px ${accentGlow};
        }
        .paper-copy-btn:active {
          transform: translateY(0);
        }
        .paper-copy-btn.copied {
          background: #ecfdf5;
          color: #059669;
          border-color: #059669;
        }
        .dark .paper-copy-btn.copied {
          background: rgba(5, 150, 105, 0.15);
          color: #34d399;
          border-color: #34d399;
        }
      `}} />
    </div>
  );
};

export default ProfitLossPage;
